import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import {blocksApi} from '../api';
import {BlocksFilter, BlockWithMetaModel, CreateBlockDTO} from '../model';
import {useCallback, useMemo, useState} from 'react';
import {useNotifications} from '../../../shared';

type BlocksState = {
  getInfiniteBlocks: UseInfiniteQueryResult<InfiniteData<BlockWithMetaModel>, Error>;
  filters: BlocksFilter;
  updateFilter: (newFilter: Partial<BlocksFilter>) => void;
  createBlockMutation: UseMutationResult<unknown, Error, CreateBlockDTO>;
};

const DEFAULT_PAGE_SIZE = 25;

export const useBlocks = (initialFilter: BlocksFilter = {}): BlocksState => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<BlocksFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));
  const {createNotification} = useNotifications();

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfiniteBlocks = useInfiniteQuery<
    BlockWithMetaModel,
    Error,
    InfiniteData<BlockWithMetaModel>,
    ['blocks', 'infinite', Omit<BlocksFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['blocks', 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return blocksApi.getBlocks({...infiniteQueryFilters, pageSize: filters.pageSize, after: pageParam});
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage, _allPages, _lastPageParam) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    staleTime: 60 * 1000,
  });

  const updateFilter = useCallback((newFilter: Partial<BlocksFilter>) => {
    setFilters((prev) => {
      return {
        ...prev,
        ...newFilter,
      };
    });
  }, []);

  const createBlockMutation = useMutation({
    mutationFn: blocksApi.createBlock,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['blocks', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'Блок успешно создан',
        name: 'createBlock',
        theme: 'success',
      });
    },
  });

  return {
    getInfiniteBlocks,
    filters,
    updateFilter,
    createBlockMutation,
  };
};
